'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  markAllNotificationsReadUseCase,
  markNotificationReadUseCase,
} from '@/features/notifications/application/usecase/mark-notifications-read';
import type { Notification } from '@/features/notifications/domain/model/notification';
import { isActive, MAX_NOTIFICATIONS } from '@/features/notifications/domain/service/notification-policy';
import { supabaseNotificationStream } from '@/features/notifications/infrastructure/adapter/supabase-notification-stream';
import { browserNotificationStateRepository } from '@/features/notifications/infrastructure/persistence/supabase-browser-notification-repository';
import {
  notificationsKeys,
  notificationsQueryOptions,
} from '@/features/notifications/presentation/queries/notifications.queries';
import {
  triggerToastNotification,
  tryShowBrowserNotification,
} from '@/features/notifications/presentation/utils/notification-alerts';

export type NotificationRow = Notification;

type NotificationsContextType = {
  notifications: NotificationRow[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  browserPermission: NotificationPermission | null;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  requestBrowserNotifications: () => Promise<void>;
  refetch: () => void;
};

const NotificationsContext = createContext<NotificationsContextType | null>(null);

type Props = {
  locationId?: string;
  children: ReactNode;
};

export function NotificationsProvider({ locationId, children }: Props) {
  const queryClient = useQueryClient();
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(() =>
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : null
  );

  const { data: notifications = [], isLoading, isError, refetch } = useQuery(notificationsQueryOptions(locationId));

  const unreadCount = useMemo(() => notifications.filter(n => n.read_at === null).length, [notifications]);

  const { mutate: markReadMutate } = useMutation({
    mutationFn: (id: string) => markNotificationReadUseCase(browserNotificationStateRepository, id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<NotificationRow[]>(notificationsKeys.list(locationId), prev =>
        (prev ?? []).map(n => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    },
  });

  const { mutate: markAllReadMutate } = useMutation({
    mutationFn: () => markAllNotificationsReadUseCase(browserNotificationStateRepository, locationId ?? ''),
    onSuccess: () => {
      const now = new Date().toISOString();
      queryClient.setQueryData<NotificationRow[]>(notificationsKeys.list(locationId), prev =>
        (prev ?? []).map(n => ({ ...n, read_at: n.read_at ?? now }))
      );
    },
  });

  const requestBrowserNotifications = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      setBrowserPermission(result);
    } catch (e) {
      console.error('Error al solicitar permiso de notificaciones:', e);
    }
  }, []);

  useEffect(() => {
    if (!locationId) return;

    return supabaseNotificationStream.subscribe(locationId, {
      onInserted: row => {
        const now = new Date().toISOString();
        if (!isActive(row, now)) return;

        queryClient.setQueryData<NotificationRow[]>(notificationsKeys.list(locationId), prev => {
          const list = prev ?? [];
          if (list.some(n => n.id === row.id)) return list;
          return [row, ...list].slice(0, MAX_NOTIFICATIONS);
        });

        triggerToastNotification(row);
        tryShowBrowserNotification(row);
      },
      onUpdated: row => {
        queryClient.setQueryData<NotificationRow[]>(notificationsKeys.list(locationId), prev =>
          (prev ?? []).map(n => (n.id === row.id ? row : n))
        );
      },
    });
  }, [locationId, queryClient]);

  const markAsRead = useCallback(
    (id: string) => {
      markReadMutate(id);
    },
    [markReadMutate]
  );

  const markAllAsRead = useCallback(() => {
    markAllReadMutate();
  }, [markAllReadMutate]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        isError,
        browserPermission,
        markAsRead,
        markAllAsRead,
        requestBrowserNotifications,
        refetch,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications debe ser usado dentro de un NotificationsProvider');
  }
  return context;
}
